import { useCallback, useEffect, useState } from "react";
import moment from "moment-timezone";
import {
  IAdjustTimeAndTextState,
  IAdjustTimeAndTextStateRef,
  IAlertTypes,
} from "../../../common/types/alertsTypes";
import {
  ALERT_ICONS,
  MIMO_LOGOS,
  ALL_TEXT_ALERT_COLOR,
  TEMPLATE_15,
  US_DAYLIGHT_TIMEZONES,
  US_DEFAULT_TIMEZONE,
  US_STANDARD_TIMEZONES,
  US_TIMEZONES_ABBREVIATIONS,
} from "../../../common/constants";
import { ITemplate } from "../../../common/types/templateTypes";
import {
  getAvailableVariantColors,
  getVariantGroup,
  templateToAlertType,
} from "../../../common/templateColorVariants";

const useAdjustTimeAndText = (
  selectedTemplate: IAlertTypes,
  onNextTab: () => void,
  isQuickAlert: boolean,
  isEditQuickAlert: boolean,
  handleTemplateClick: (templateData: IAlertTypes) => void,
  isDateTimeFieldEmpty: (isAnyFieldEmpty: boolean) => void,
  adjustTimeAndTextState: IAdjustTimeAndTextState,
  setAdjustTimeAndTextState: (state: IAdjustTimeAndTextState) => void,
  adjustTimeAndTextStateRef: IAdjustTimeAndTextStateRef,
  allClearChecked: boolean,
  setAllClearCheckbox: (allClearChecked: boolean) => void,
  allClearTime: string,
  templateLookup: Map<number, ITemplate>,
) => {
  const hideIcon: boolean = !!selectedTemplate?.templateReqIcon;
  const showLogo: boolean = !!selectedTemplate?.templateReqLogo;
  const hideHeading: boolean = !!selectedTemplate?.templateReqHeading;
  const hideAlertMessage: boolean = !!selectedTemplate?.templateReqMessage;
  const showZipCode: boolean = !!selectedTemplate?.templateReqZipCode;
  const variantGroup = selectedTemplate?.id ? getVariantGroup(selectedTemplate.id) : undefined;
  const hideAllTextColor: boolean = !!variantGroup;

  const [isSendNowChecked, setIsSendNowChecked] = useState<boolean>(
    adjustTimeAndTextState.isCheckedNowState
  );
  const [startDate, setStartDate] = useState<Date>(
    adjustTimeAndTextState.startDateState
  );
  const [startDateString, setStartDateString] = useState<string>(
    adjustTimeAndTextState.startDateString
  );
  const [endDateString, setEndDateString] = useState<string>(
    adjustTimeAndTextState.endDateString
  );
  const [startTime, setStartTime] = useState<string>(
    adjustTimeAndTextState.startTimeState
  );
  const [endTime, setEndTime] = useState<string>(
    adjustTimeAndTextState.endTimeState
  );
  const [timeZone, setTimeZone] = useState<string>(
    adjustTimeAndTextState.timeZoneState
  );

  const minimumSelectableDate = useCallback(() => {
  const timezoneAbbreviations = US_TIMEZONES_ABBREVIATIONS;
  const selectedTimezone = timezoneAbbreviations[timeZone];

  if (!selectedTimezone) {
    return new Date();
  }

  return moment.tz(selectedTimezone).startOf("day").toDate();
}, [timeZone]);

  const [endDate, setEndDate] = useState<Date>(
    adjustTimeAndTextState.endDateState
  );
  const [severityLevel, setSeverityLevel] = useState<string>(
    adjustTimeAndTextState.severityLevelState.toLowerCase()
  );
  const [isAnyFieldEmpty, setIsAnyFieldEmpty] = useState<boolean>(true);

  const rawMessage = (selectedTemplate?.maxAlertMessageCharacter ?? 0) > 0 ? adjustTimeAndTextState?.alertMessageState : "";
  const parts = rawMessage.split(" ");
  const firstPart = parts[0] || "";
  const restMessage = parts.slice(1).join(" ");

  const isTemplate2 = selectedTemplate?.id === 2;
  const startsWithPic = firstPart.toLowerCase().startsWith("pic");

  const shouldSplit = isTemplate2 && startsWithPic;

  const [alertMessage, setAlertMessage] = useState<string>(
    shouldSplit ? restMessage : rawMessage
  );

  const [alertHeading, setAlertHeading] = useState<string>(
    (selectedTemplate?.maxAlertTitleCharacter ?? 0) > 0 ? (adjustTimeAndTextState?.alertHeadingState ?? "") : ""
  );

  const [zipCode, setZipCode] = useState<string>(
    showZipCode ? (shouldSplit ? restMessage : rawMessage) : ""
  );

  const [alertIcon, setAlertIcon] = useState<string>(
    hideIcon
      ? shouldSplit
        ? firstPart
        : rawMessage.split(" ")[0]
      : "Select Template"
  );

  const [allTextColor, setAllTextColor] = useState<number>(
    selectedTemplate?.id ?? 0
  );

  useEffect(() => {
    if (selectedTemplate?.id) {
      setAllTextColor(selectedTemplate.id);
    }
  }, [selectedTemplate?.id]);

  const [mimoLogo, setMimoLogo] = useState<string>(
    showLogo ? (shouldSplit ? firstPart : rawMessage) : "Select Logo"
  );

  const [alertMessageNewLine, setAlertMessageNewLine] = useState<number>(0);

  const isDSTActive = (timezone) => {
    const now = moment();
    const start = moment
      .tz(now.year() + "-03-01", timezone)
      .startOf("month")
      .day(0)
      .add(7, "days")
      .day(0); // Second Sunday in March
    const end = moment
      .tz(now.year() + "-11-01", timezone)
      .startOf("month")
      .day(0)
      .add(7, "days")
      .day(0); // First Sunday in November
    return now.isBetween(start, end, null, "[]");
  };

  const dstStatus: boolean = isDSTActive(US_DEFAULT_TIMEZONE);

  const timeZones = dstStatus ? US_DAYLIGHT_TIMEZONES : US_STANDARD_TIMEZONES;
  const alertIcons = ALERT_ICONS;
  const allTextColors = variantGroup
    ? getAvailableVariantColors(variantGroup, templateLookup)
    : ALL_TEXT_ALERT_COLOR;
  const mimoLogos = MIMO_LOGOS;

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSeverityLevel(event?.target?.value);
  };

  const handleIconChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setAlertIcon(event.target.value);
  };

  const handleAllTextColorChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newId = Number(event.target.value);
    const found = templateLookup.get(newId);
    // Only switch templateId when the selected color maps to an org-available template.
    // Previously the UI could show Orange/Red while still submitting the first/representative ID.
    if (!found) {
      return;
    }
    setAllTextColor(newId);
    handleTemplateClick(templateToAlertType(found));
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setMimoLogo(event.target.value);
  };

  const handleStartTimeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setStartTime(event?.target?.value);
  };

  const handleEndTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndTime(event?.target?.value);
  };

  const handleTimeZoneChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setTimeZone(event?.target?.value);
  };

  const handleStartDateChange = (event) => {
    setStartDate(event);
    setStartDateString(getDate(event));
  };

  const handleEndDateChange = (event) => {
    setEndDate(event);
    setEndDateString(getDate(event));
  };

  const handleAllClearCheckboxChange = (allClearChecked: boolean) => {
    if (allClearChecked) {
      setIsSendNowChecked(false);
    }
    setAllClearCheckbox(allClearChecked);
  };
  const getDate = (event: string) => {
    const dateObject: Date = new Date(event);
    const day: string = dateObject.getDate().toString().padStart(2, "0");
    const month: string = (dateObject.getMonth() + 1)
      .toString()
      .padStart(2, "0");
    const year: number = dateObject.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getUTCDate = (date: string, time: string, timezone: string) => {
    const timezoneAbbreviations = US_TIMEZONES_ABBREVIATIONS;
    const dateTimeString = `${date} ${time}`;
    const utcDateTime = moment
      .tz(dateTimeString, "DD/MM/YYYY HH:mm", timezoneAbbreviations[timeZone])
      .utc();
    return utcDateTime.toISOString();
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event?.target?.checked) {
      setAllClearCheckbox(false);
    }
    setIsSendNowChecked(event?.target?.checked);
  };

  useEffect(() => {
    if (
      (hideIcon && alertIcon === "Select Template") ||
      (hideAlertMessage && !alertMessage) ||
      (hideHeading && !alertHeading) ||
      (showLogo && mimoLogo === "Select Logo") ||
      (showZipCode && !zipCode)
    ) {
      setIsAnyFieldEmpty(true);
    } else if (
      (isSendNowChecked && !severityLevel) ||
      (allClearChecked && (!severityLevel || allClearTime.trim() === ""))
    ) {
      setIsAnyFieldEmpty(true);
    } else if (
      !isQuickAlert &&
      !isEditQuickAlert &&
      !isSendNowChecked &&
      !allClearChecked &&
      (!startDate ||
        !endDate ||
        !startTime ||
        !endTime ||
        !timeZone ||
        !severityLevel)
    ) {
      setIsAnyFieldEmpty(true);
    } else {
      setIsAnyFieldEmpty(false);
    }

    if ((isQuickAlert || isEditQuickAlert) && severityLevel.trim() === "") {
      setIsAnyFieldEmpty(true);
    }

    if (!isQuickAlert && !isEditQuickAlert) {
      let startDateTime: Date = new Date(
        getUTCDate(startDateString, startTime, timeZone)
      );
      let endDateTime: Date = new Date(
        getUTCDate(endDateString, endTime, timeZone)
      );

      if (
        endDateTime <= startDateTime &&
        !isSendNowChecked &&
        !allClearChecked
      ) {
        setIsAnyFieldEmpty(true);
      }
      isDateTimeFieldEmpty(isAnyFieldEmpty);
    }
    else
    {
      isDateTimeFieldEmpty(isAnyFieldEmpty);
    }
  }, [
    startDate,
    endDate,
    startTime,
    endTime,
    alertMessage,
    alertHeading,
    alertIcon,
    mimoLogo,
    zipCode,
    timeZone,
    isSendNowChecked,
    isDateTimeFieldEmpty,
    isAnyFieldEmpty,
    severityLevel,
    allClearChecked,
    allClearTime,
  ]);

  const updateState = (): void => {
    let startDateTime: string = getUTCDate(
      startDateString,
      startTime,
      timeZone
    );
    let endDateTime: string = getUTCDate(endDateString, endTime, timeZone);

    if (isSendNowChecked || allClearChecked) {
      startDateTime = "";
      endDateTime = "";
      setStartDateString("");
      setEndDateString("");
      setStartTime("");
      setEndTime("");
      setSeverityLevel("");
    }
    let message: string = alertMessage;

    if (selectedTemplate.templateReqIcon) {
      if (selectedTemplate.id === 15) {
        message = `${alertMessage}`;
      } else {
        message = `${alertIcon} ${alertMessage}`;
      }
    } else if (selectedTemplate.id === 1) {
      message = `${alertMessage}`;
    } else if (selectedTemplate.id === 3) {
      message = `${alertMessage}`;
    } else if (selectedTemplate.id === 4) {
      message = `Alert with Logo`;
    } else if (selectedTemplate.id === 12) {
      message = `${alertMessage}`;
    } else if (selectedTemplate.id === 13) {
      message = `Device IP and MAC Address `;
    } else if (selectedTemplate.id === 14) {
      message = `${alertMessage}`;
    } else if (selectedTemplate.id === 16) {
      message = `${alertMessage}`;
    } else if (selectedTemplate.id === 17) {
      message = `${zipCode}`;
    } else if (selectedTemplate.id === 18) {
      message = `${alertMessage}`;
    } else if (selectedTemplate.id === 19) {
      message = `${alertMessage}`;
    } else if (selectedTemplate.templateReqLogo) {
      message = `${mimoLogo} ${alertMessage}`;
    }

    const updatedState: IAdjustTimeAndTextState = {
      ...adjustTimeAndTextState,
      startDateState: startDate,
      endDateState: endDate,
      startTimeState: startTime,
      endTimeState: endTime,
      timeZoneState: timeZone,
      severityLevelState: severityLevel,
      alertMessageState: message,
      alertHeadingState: alertHeading,
      startUTCDate: startDateTime,
      endUTCDate: endDateTime,
      isCheckedNowState: isSendNowChecked,
      startDateString: startDateString,
      endDateString: endDateString,
    };

    setAdjustTimeAndTextState(updatedState);
  };

  const handleNextButtonClick = (): void => {
    updateState();
    onNextTab();
  };

  if (adjustTimeAndTextStateRef) {
    adjustTimeAndTextStateRef.updateAdjustTimeAndTextState = updateState;
  }

  const handleAlertMessageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newLines = event.target.value?.match(/\n/g)?.length ?? 0;
    setAlertMessageNewLine(newLines);
    const maxChars = selectedTemplate?.maxAlertMessageCharacter ?? 0;
    const maxLines = selectedTemplate?.maxAlertMessageLine ?? 0;
    if (
      event.target.value?.length <= maxChars &&
      newLines < maxLines
    ) {
      setAlertMessage(event?.target?.value);
    }
  };

  const handleZipCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event?.target?.value;
    const zipPattern = /^\d{0,5}(-\d{0,4})?$/;

    if (zipPattern.test(input)) {
      setZipCode(input);
    }
  };
  const handleAlertHeadingChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const maxChars = selectedTemplate?.maxAlertTitleCharacter ?? 0;
    if (event.target.value?.length <= maxChars) {
      setAlertHeading(event?.target?.value);
    }
  };

  return {
    isSendNowChecked,
    handleCheckboxChange,
    handleStartDateChange,
    handleEndDateChange,
    startTime,
    handleStartTimeChange,
    endTime,
    handleEndTimeChange,
    timeZone,
    handleTimeZoneChange,
    severityLevel,
    handleRadioChange,
    alertMessage,
    alertHeading,
    handleAlertMessageChange,
    handleAlertHeadingChange,
    handleNextButtonClick,
    isAnyFieldEmpty,
    startDate,
    endDate,
    timeZones,
     minimumSelectableDate: minimumSelectableDate(),
    alertIcon,
    mimoLogo,
    zipCode,
    alertIcons,
    allTextColors,
    allTextColor,
    setAllTextColor,
    handleIconChange,
    handleAllTextColorChange,
    hideIcon,
    hideAllTextColor,
    showLogo,
    hideHeading,
    hideAlertMessage,
    mimoLogos,
    handleLogoChange,
    showZipCode,
    handleZipCodeChange,
    alertMessageNewLine,
    handleAllClearCheckboxChange,
  };
};

export default useAdjustTimeAndText;
